export const BIRTH_TIME_OPTIONS = [
  '不確定／不知道',
  '子時｜23:00–00:59',
  '丑時｜01:00–02:59',
  '寅時｜03:00–04:59',
  '卯時｜05:00–06:59',
  '辰時｜07:00–08:59',
  '巳時｜09:00–10:59',
  '午時｜11:00–12:59',
  '未時｜13:00–14:59',
  '申時｜15:00–16:59',
  '酉時｜17:00–18:59',
  '戌時｜19:00–20:59',
  '亥時｜21:00–22:59',
];

export const DEFAULT_SERVICE_RESPONSE_POLICY = {
  mode: 'per_unit',
  quantityPerResponse: 1,
  minimumQuantity: 1,
  responsesPerStep: 1,
};

const cleanText = (value) => String(value ?? '').trim();

export const isAnswerGroupCountAdjustEnabled = (template = {}) => (
  template?.allowAnswerGroupCountAdjust !== false
);

export const labelServiceAnswerPlan = (index = 0, template = {}) => (
  isAnswerGroupCountAdjustEnabled(template)
    ? `答案組 ${Number(index) + 1}`
    : `第 ${Number(index) + 1} 份`
);

export const canRemoveServiceAnswerPlan = (plans = [], template = {}) => (
  isAnswerGroupCountAdjustEnabled(template) && Array.isArray(plans) && plans.length > 1
);

export const getServiceAnswerPlanSameAsSource = ({
  plans = [],
  planIndex = 0,
  template = {},
  fallbackPlanId = '',
  fallbackLabel = '上一項商品',
} = {}) => {
  if (template?.allowSameAsPrevious !== true) {
    return { planId: '', label: fallbackLabel };
  }
  if (Number(planIndex) > 0 && plans[0]?.id) {
    return { planId: plans[0].id, label: '第 1 份' };
  }
  return { planId: fallbackPlanId || '', label: fallbackLabel };
};

export const buildDefaultServiceAnswerPlans = (serviceGroup = {}, template = {}) => {
  const tasks = Array.isArray(serviceGroup?.tasks) ? serviceGroup.tasks : [];
  if (tasks.length === 0) return [];
  if (!isAnswerGroupCountAdjustEnabled(template)) {
    return tasks
      .filter((task) => task?.id)
      .map((task) => ({ id: task.id, count: 1 }));
  }
  return tasks[0]?.id ? [{ id: tasks[0].id, count: tasks.length }] : [];
};

export const areServiceAnswerPlansCompatible = (plans = [], serviceGroup = {}, template = {}) => {
  const tasks = Array.isArray(serviceGroup?.tasks) ? serviceGroup.tasks : [];
  if (!Array.isArray(plans) || plans.length === 0 || tasks.length === 0) return false;
  const hasValidCounts = plans.every((plan) => Number.isInteger(Number(plan?.count)) && Number(plan.count) > 0);
  const assignedCount = plans.reduce((sum, plan) => sum + Number(plan?.count || 0), 0);
  if (!hasValidCounts || assignedCount !== tasks.length) return false;
  if (isAnswerGroupCountAdjustEnabled(template)) return true;
  return plans.length === tasks.length && plans.every((plan) => Number(plan.count) === 1);
};

export const normalizeServiceAnswerPlansForGroups = (
  serviceGroups = [],
  templates = {},
  existingPlansByGroup = {},
) => Object.fromEntries((serviceGroups || []).map((serviceGroup) => {
  const firstTask = serviceGroup?.tasks?.[0] || {};
  const templateId = firstTask.questionTemplateId || firstTask.formId || '';
  const template = templates?.[templateId] || {};
  const existing = existingPlansByGroup?.[serviceGroup.key] || [];
  const plans = areServiceAnswerPlansCompatible(existing, serviceGroup, template)
    ? existing
    : buildDefaultServiceAnswerPlans(serviceGroup, template);
  return [serviceGroup.key, plans];
}));

export const buildServiceQuestionFlow = (questions = []) => {
  const indexedQuestions = new Map(
    questions.map((question, index) => [cleanText(question?.id), { question, index }]),
  );

  return questions.map((question, index) => {
    const parentId = cleanText(question?.condition?.questionId);
    const parentEntry = parentId ? indexedQuestions.get(parentId) : null;
    const parent = parentEntry?.question || null;
    const conditionValues = (question?.condition?.values || []).map(cleanText).filter(Boolean);
    const hasCondition = Boolean(parentId);
    const isConditionValid = !hasCondition || Boolean(parent && parentEntry.index < index);

    return {
      id: cleanText(question?.id) || `question-${index + 1}`,
      index,
      number: index + 1,
      label: cleanText(question?.label) || `問題 ${index + 1}`,
      type: question?.type || 'text',
      branching: question?.branching === true,
      required: question?.required === true,
      hasCondition,
      isConditionValid,
      parentId,
      parentLabel: cleanText(parent?.label),
      conditionValues,
    };
  });
};

export const getServiceQuestionOrderError = (questions = []) => {
  const flow = buildServiceQuestionFlow(questions);
  const invalid = flow.find((item) => item.hasCondition && !item.isConditionValid);
  if (!invalid) return '';
  const parent = questions.find((question) => cleanText(question?.id) === invalid.parentId);
  const parentLabel = cleanText(parent?.label) || '分流題目';
  return `「${parentLabel}」必須排在「${invalid.label}」前面，才能保留顯示條件。`;
};

export const moveServiceQuestion = (questions = [], fromIndex, toIndex) => {
  if (
    !Number.isInteger(fromIndex) ||
    !Number.isInteger(toIndex) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= questions.length ||
    toIndex >= questions.length ||
    fromIndex === toIndex
  ) {
    return { questions, error: '' };
  }

  const reordered = [...questions];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);
  const error = getServiceQuestionOrderError(reordered);
  return error ? { questions, error } : { questions: reordered, error: '' };
};

export const serviceAnswerIsFilled = (value) => {
  if (Array.isArray(value)) return value.some(serviceAnswerIsFilled);
  if (value && typeof value === 'object') return Object.values(value).some(serviceAnswerIsFilled);
  return cleanText(value) !== '';
};

export const normalizeServiceResponsePolicy = (policy = {}) => {
  const mode = ['per_unit', 'per_n', 'minimum_once', 'minimum_then_per_n'].includes(policy?.mode)
    ? policy.mode
    : DEFAULT_SERVICE_RESPONSE_POLICY.mode;
  const quantityPerResponse = Math.max(
    1,
    Math.floor(Number(policy?.quantityPerResponse || policy?.threshold || 1) || 1),
  );
  const minimumQuantity = Math.max(
    1,
    Math.floor(Number(policy?.minimumQuantity || policy?.minimumThreshold || 1) || 1),
  );
  const responsesPerStep = Math.max(
    1,
    Math.floor(Number(policy?.responsesPerStep || policy?.responsesPerGroup || 1) || 1),
  );
  return { mode, quantityPerResponse, minimumQuantity, responsesPerStep };
};

export const calculateServiceResponseQuota = (quantity, policy = {}) => {
  const total = Math.max(0, Math.floor(Number(quantity || 0) || 0));
  const normalized = normalizeServiceResponsePolicy(policy);
  if (normalized.mode === 'per_n') {
    return Math.floor(total / normalized.quantityPerResponse);
  }
  if (normalized.mode === 'minimum_once') {
    return total >= normalized.quantityPerResponse ? 1 : 0;
  }
  if (normalized.mode === 'minimum_then_per_n') {
    return total >= normalized.minimumQuantity ? total : 0;
  }
  return total;
};

const answerMatchesCondition = (answer, expectedValues = []) => {
  const expected = new Set((expectedValues || []).map(cleanText).filter(Boolean));
  if (expected.size === 0) return true;
  if (Array.isArray(answer)) return answer.some((value) => expected.has(cleanText(value)));
  return expected.has(cleanText(answer));
};

export const isServiceQuestionVisible = (question, answers = {}, questions = []) => {
  const condition = question?.condition;
  const parentId = cleanText(condition?.questionId);
  if (!parentId) return true;
  const questionIndex = questions.findIndex((item) => item?.id === question?.id);
  const parentIndex = questions.findIndex((item) => item?.id === parentId);
  if (parentIndex < 0 || (questionIndex >= 0 && parentIndex >= questionIndex)) return false;
  const parent = questions[parentIndex];
  if (!isServiceQuestionVisible(parent, answers, questions)) return false;
  return answerMatchesCondition(answers[parentId], condition?.values);
};

export const getVisibleServiceQuestions = (questions = [], answers = {}) => (
  questions.filter((question) => isServiceQuestionVisible(question, answers, questions))
);

export const sanitizeVisibleServiceResponses = (questions = [], answers = {}) => {
  const visibleIds = new Set(getVisibleServiceQuestions(questions, answers).map((question) => question.id));
  return Object.fromEntries(
    Object.entries(answers || {}).filter(([questionId]) => visibleIds.has(questionId)),
  );
};

export const validateCalendarDateAnswer = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if (!['gregorian', 'lunar'].includes(value.calendar)) return false;
  const year = Number(value.year);
  const month = Number(value.month);
  const day = Number(value.day);
  if (!Number.isInteger(year) || year < 1 || year > 9999) return false;
  if (!Number.isInteger(month) || month < 1 || month > 12) return false;
  if (!Number.isInteger(day) || day < 1) return false;
  const today = new Date();
  if (year > today.getFullYear()) return false;
  if (value.calendar === 'lunar') return day <= 30;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return false;
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return date <= todayDate;
};

const validateLegacyDateAnswer = (value, dateMode = 'date') => {
  const [datePart = '', timePart = ''] = cleanText(value).split('T');
  const [yearText = '', monthText = '', dayText = ''] = datePart.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (!year || !month || !day) return false;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return false;
  return dateMode !== 'datetime' || /^\d{2}:\d{2}/.test(timePart);
};

export const validateServiceQuestionAnswer = (question = {}, value) => {
  const text = cleanText(Array.isArray(value) ? value.join(',') : value);
  if (question.required && !serviceAnswerIsFilled(value)) return '必填未填寫';
  if (question.type === 'text') {
    const min = Number(question.minLength || 0) || 0;
    const max = Number(question.maxLength || 0) || 0;
    if (min > 0 && text.length < min) return `字數少於至少字數：至少 ${min} 個字，目前 ${text.length} 個字`;
    if (max > 0 && text.length > max) return `字數超過上限：最多 ${max} 個字，目前 ${text.length} 個字`;
  }
  if (question.type === 'calendar_date' && serviceAnswerIsFilled(value) && !validateCalendarDateAnswer(value)) {
    return '請完整選擇國曆或農曆與年月日';
  }
  if (question.type === 'date' && text && !validateLegacyDateAnswer(value, question.dateMode || 'date')) {
    return question.dateMode === 'datetime' ? '請完整選擇日期與時間' : '請完整選擇年月日';
  }
  if (question.type === 'birth_time' && text && !BIRTH_TIME_OPTIONS.includes(text)) {
    return '請選擇正確的時辰';
  }
  if (question.type === 'choice' && text && !(question.options || []).map(cleanText).includes(text)) {
    return '請選擇有效選項';
  }
  return '';
};

export const getServiceAnswerPlanProgress = (questions = [], plans = [], answersByPlan = {}) => {
  const rows = (plans || []).map((plan) => {
    const answers = answersByPlan?.[plan.id] || {};
    const visibleQuestions = getVisibleServiceQuestions(questions, answers);
    const errorCount = visibleQuestions.reduce((count, question) => (
      count + (validateServiceQuestionAnswer(question, answers[question.id]) ? 1 : 0)
    ), 0);
    return {
      id: plan.id,
      count: Number(plan.count || 0),
      errorCount,
      complete: errorCount === 0,
    };
  });
  const complete = rows.filter((row) => row.complete).length;
  return {
    rows,
    total: rows.length,
    complete,
    incomplete: rows.length - complete,
  };
};

export const formatCalendarDateAnswer = (value) => {
  if (!validateCalendarDateAnswer(value)) return '';
  const prefix = value.calendar === 'lunar' ? '農曆' : '國曆';
  const month = String(value.month).padStart(2, '0');
  const day = String(value.day).padStart(2, '0');
  return `${prefix} ${value.year}-${month}-${day}`;
};
